import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Icon, Layout, LogoLoader } from '@loadsmart/miranda-react'

import { type StopAppointment, isAppointmentResetable } from 'core/appointments/domain/Appointment'
import { RequestStatus } from 'core/domain/Request'

import { useCancelOpenDockWarehouseAppointment } from '../hooks/useCancelOpenDockWarehouseAppointment'
import { useGetOpenDockStopAppointment } from '../hooks/useGetOpenDockStopAppointment'
import { useGetOpenDockWarehouseDetails } from '../hooks/useGetOpenDockWarehouseDetails'
import { AppointmentCancelDialog } from './AppointmentCancelDialog'
import { StepContent } from './StepContent'

const STEPS = {
  SCHEDULED: 'scheduled',
  RESCHEDULE: 'reschedule',
} as const

export type OpenDockAppointmentScheduledDrawerProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  appointment?: StopAppointment
  facilityName?: string
  facilityTimezone?: string
  shipmentUUID: string
  stopIndex: number
}

export function OpenDockAppointmentScheduledDrawer({
  isOpen,
  onClose,
  onConfirm,
  appointment,
  facilityName,
  facilityTimezone,
  shipmentUUID,
  stopIndex,
}: OpenDockAppointmentScheduledDrawerProps) {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [step, setStep] = useState<string>(STEPS.SCHEDULED)

  const {
    stopAppointment,
    status: stopStatus,
    fetchOpenDockStopAppointment,
  } = useGetOpenDockStopAppointment(shipmentUUID, stopIndex, isOpen)

  const { warehouseDetails, status: warehouseStatus } = useGetOpenDockWarehouseDetails(
    shipmentUUID,
    stopIndex,
    isOpen
  )

  useEffect(() => {
    if (isOpen) {
      setStep(STEPS.SCHEDULED)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && (stopStatus === RequestStatus.Error || warehouseStatus === RequestStatus.Error)) {
      toast.error('Failed to load OpenDock appointment data. Please try again later.')
      onClose()
    }
  }, [isOpen, stopStatus, warehouseStatus, onClose])

  const fetchFailed =
    isOpen && (stopStatus === RequestStatus.Error || warehouseStatus === RequestStatus.Error)

  const showStepContent =
    isOpen &&
    stopStatus === RequestStatus.Done &&
    warehouseStatus === RequestStatus.Done &&
    Boolean(warehouseDetails) &&
    Boolean(stopAppointment)

  const showLoader = isOpen && !fetchFailed && !showStepContent

  const { call, isPending, lastOptionRef, requestStatus, resetMutation } =
    useCancelOpenDockWarehouseAppointment()

  useEffect(() => {
    if (isCancelDialogOpen) {
      resetMutation()
    }
  }, [isCancelDialogOpen, resetMutation])

  useEffect(() => {
    if (requestStatus !== RequestStatus.Done) {
      return
    }
    const option = lastOptionRef.current
    if (option === 'reset') {
      toast.success('Appointment reset successfully')
    } else {
      toast.success('Appointment canceled successfully')
    }
    setIsCancelDialogOpen(false)
    if (option !== 'reset') {
      fetchOpenDockStopAppointment()
    }
    onConfirm()
    resetMutation()
  }, [fetchOpenDockStopAppointment, lastOptionRef, onConfirm, requestStatus, resetMutation])

  useEffect(() => {
    if (requestStatus !== RequestStatus.Error) {
      return
    }
    const option = lastOptionRef.current
    if (option === 'reset') {
      toast.error('Could not reset appointment, try again later')
    } else {
      toast.error('Could not cancel appointment, try again later')
    }
  }, [lastOptionRef, requestStatus])

  return (
    <>
      <Drawer open={isOpen} onClose={onClose}>
        {showLoader && (
          <>
            <Drawer.Header>
              <Layout.Group align="center" gap="spacing-1">
                <Icon name="calendar" size="24px" />
                Scheduled
              </Layout.Group>
              <Drawer.Close />
            </Drawer.Header>
            <Drawer.Body>
              <Layout.Stack align="center" paddingT="spacing-16">
                <LogoLoader />
              </Layout.Stack>
            </Drawer.Body>
            <Drawer.Actions>
              <Drawer.ActionTertiary disabled style={{ marginRight: 'auto' }}>
                Cancel appointment
              </Drawer.ActionTertiary>
              <Drawer.ActionPrimary disabled>Reschedule</Drawer.ActionPrimary>
            </Drawer.Actions>
          </>
        )}
        {showStepContent && warehouseDetails && stopAppointment && (
          <StepContent
            step={step}
            setStep={setStep}
            appointment={appointment}
            stopAppointment={stopAppointment}
            warehouseDetails={warehouseDetails}
            facilityName={facilityName}
            facilityTimezone={facilityTimezone}
            shipmentUUID={shipmentUUID}
            stopIndex={stopIndex}
            onCancelAppointment={() => setIsCancelDialogOpen(true)}
            onConfirm={onConfirm}
          />
        )}
      </Drawer>
      <AppointmentCancelDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        isLoading={isPending}
        onConfirm={option => call({ option, shipmentUUID, stopIndex })}
        resetOnly={isAppointmentResetable(stopAppointment)}
      />
    </>
  )
}
